import React from 'react';
import { ArrowLeft } from 'lucide-react';
import MarketingHeader from './components/MarketingHeader';
import LegalFooter from './components/LegalFooter';
import { REQUEST_ACCESS_URL } from './config/links';
import useDocumentMeta from './hooks/useDocumentMeta';

const MachiningProgram = () => {

  useDocumentMeta({
    title: 'Machining Program — Design it. Prove it. We build it. | Factory OS',
    description: 'The Factory OS Machining Program fabricates parts for teams that complete a full design cycle on the platform — requirements, CAD, DFM review, analysis, and a Design → Build → Prove record. FDM 3D printing is free for anyone on the platform; CNC milling and more processes are coming online.',
    path: '/machining',
  });

  return (
    <div className="fos-notebook min-h-screen">
      <MarketingHeader />

      {/* ══════════ HERO ══════════ */}
      <section className="pt-14 sm:pt-24 pb-10 sm:pb-16 px-5">
        <div className="max-w-[1080px] mx-auto">
          <a href="/" className="inline-flex items-center gap-2 no-underline mb-8" style={{ fontFamily: 'var(--mono)', fontSize: 'var(--t-sm)', color: 'var(--ink-3)', letterSpacing: '0.4px' }}>
            <ArrowLeft size={14} /> Back to Factory OS
          </a>

          <div className="flex flex-wrap items-center gap-3 mb-6">
            <span className="nb-chip" style={{ borderColor: 'var(--nb-accent)', color: 'var(--nb-accent)', background: 'var(--accent-wash)' }}>
              <span style={{ fontSize: 'var(--t-sm)' }}>●</span> LIMITED CAPACITY
            </span>
          </div>

          <h1 style={{ fontFamily: 'var(--hand)', fontWeight: 700, fontSize: 'var(--t-lg)', lineHeight: 1.05, color: 'var(--ink)', letterSpacing: '-0.5px' }}>
            Design it. Prove it.<br />
            <span className="nb-under">Then we'll build it for you.</span>
          </h1>

          <p className="mt-5 max-w-[620px]" style={{ fontFamily: 'var(--hand)', fontSize: 'var(--t-md)', color: 'var(--ink-2)', lineHeight: 1.45 }}>
            The Factory OS Machining Program provides fabrication support to hardware startups and engineering teams that complete a full design cycle on the platform. Prove your engineering and we'll cut the metal.
          </p>

          <div className="flex flex-wrap gap-3 mt-7">
            <a href={REQUEST_ACCESS_URL} className="nb-btn no-underline">
              Request access
            </a>
            <a href="https://demo.factory-os.com" className="nb-btn nb-btn-ghost">
              ▶ Take the 2-minute tour
            </a>
          </div>
        </div>
      </section>

      {/* ══════════ WHAT THIS IS / ISN'T ══════════ */}
      <section className="py-12 md:py-[72px] px-5" style={{ borderTop: '1px dashed var(--ink-4)' }}>
        <div className="max-w-[1080px] mx-auto">
          <div className="max-w-[720px] mb-7">
            <div className="nb-eyebrow">clarity</div>
            <h2 className="mt-2" style={{ fontFamily: 'var(--hand)', fontWeight: 700, fontSize: 'var(--t-lg)', lineHeight: 1.1, color: 'var(--ink)' }}>
              Let's be clear about what this is.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="nb-ba-before p-5">
              <h3 style={{ fontFamily: 'var(--mono)', fontSize: 'var(--t-sm)', color: 'var(--ink-3)', letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: 10 }}>This is NOT</h3>
              <ul className="space-y-2.5 list-none p-0 m-0">
                {[
                  'A free machine shop',
                  'A service for incomplete designs',
                  'A place to submit undocumented parts',
                  'A replacement for Xometry or SendCutSend',
                  'A way to bypass engineering rigor',
                ].map((item, i) => (
                  <li key={i} className="relative pl-6" style={{ fontFamily: 'var(--hand)', fontSize: 'var(--t-md)', color: 'var(--ink-2)', lineHeight: 1.35 }}>
                    <span className="absolute left-0" style={{ fontFamily: 'var(--mono)', color: 'var(--ink-4)' }}>✗</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="nb-ba-after p-5">
              <h3 style={{ fontFamily: 'var(--mono)', fontSize: 'var(--t-sm)', color: 'var(--nb-accent)', letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: 10 }}>This IS</h3>
              <ul className="space-y-2.5 list-none p-0 m-0">
                {[
                  'A proof-based fabrication program',
                  'Tied to completed Factory OS design cycles',
                  'A reward for disciplined engineering',
                  'Selective and merit-based',
                  'Built to reinforce Design → Build → Prove',
                ].map((item, i) => (
                  <li key={i} className="relative pl-6" style={{ fontFamily: 'var(--hand)', fontSize: 'var(--t-md)', color: 'var(--ink-2)', lineHeight: 1.35 }}>
                    <span className="absolute left-0" style={{ fontFamily: 'var(--mono)', color: 'var(--nb-green)' }}>✓</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════ WHY THIS EXISTS ══════════ */}
      <section className="py-12 md:py-[72px] px-5" style={{ borderTop: '1px dashed var(--ink-4)' }}>
        <div className="max-w-[720px] mx-auto text-center">
          <div className="nb-eyebrow">why</div>
          <h2 className="mt-2" style={{ fontFamily: 'var(--hand)', fontWeight: 700, fontSize: 'var(--t-lg)', lineHeight: 1.1, color: 'var(--ink)' }}>
            Most teams build parts without validated requirements.
          </h2>
          <p className="mt-3" style={{ fontFamily: 'var(--hand)', fontSize: 'var(--t-md)', color: 'var(--ink-2)' }}>
            Decisions aren't documented. Knowledge is lost every cycle. This program rewards teams who break that pattern.
          </p>
          <p className="mt-4" style={{ fontFamily: 'var(--script)', fontSize: 'var(--t-md)', color: 'var(--nb-accent)' }}>
            Prove your engineering is real, and we'll help you manufacture it.
          </p>
        </div>
      </section>

      {/* ══════════ ELIGIBILITY ══════════ */}
      <section className="py-12 md:py-[72px] px-5" style={{ borderTop: '1px dashed var(--ink-4)' }}>
        <div className="max-w-[1080px] mx-auto">
          <div className="max-w-[720px] mb-7">
            <div className="nb-eyebrow" style={{ color: 'var(--nb-accent)' }}>hard gate</div>
            <h2 className="mt-2" style={{ fontFamily: 'var(--hand)', fontWeight: 700, fontSize: 'var(--t-lg)', lineHeight: 1.1, color: 'var(--ink)' }}>
              Eligibility requirements
            </h2>
            <p className="mt-2" style={{ fontFamily: 'var(--hand)', fontSize: 'var(--t-md)', color: 'var(--ink-2)' }}>
              Every submitted part must exist in Factory OS with:
            </p>
          </div>

          <div className="nb-sketch" style={{ maxWidth: 620 }}>
            <ul className="space-y-4 list-none p-0 m-0">
              {[
                'Defined requirements with acceptance criteria',
                'Linked CAD model',
                'DFM / manufacturability review',
                'Supporting analysis or engineering justification',
                'Completed Design → Build → Prove cycle',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ border: '1.5px solid var(--ink)', background: 'var(--paper)', fontFamily: 'var(--mono)', fontWeight: 700, fontSize: 'var(--t-sm)', boxShadow: '2px 3px 0 rgba(0,0,0,0.10)' }}
                  >
                    {i + 1}
                  </div>
                  <span style={{ fontFamily: 'var(--hand)', fontSize: 'var(--t-md)', color: 'var(--ink)', lineHeight: 1.4 }}>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="nb-sticky mt-5" style={{ transform: 'rotate(-1.2deg)' }}>
            <b>If it's not traceable, it's not eligible.</b>
          </div>
        </div>
      </section>

      {/* ══════════ SHOP CAPABILITIES ══════════ */}
      <section className="py-12 md:py-[72px] px-5" style={{ borderTop: '1px dashed var(--ink-4)' }}>
        <div className="max-w-[1080px] mx-auto">
          <div className="max-w-[720px] mb-7">
            <div className="nb-eyebrow">the shop</div>
            <h2 className="mt-2" style={{ fontFamily: 'var(--hand)', fontWeight: 700, fontSize: 'var(--t-lg)', lineHeight: 1.1, color: 'var(--ink)' }}>
              What we can build
            </h2>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {[
              { name: 'FDM 3D printing', status: 'live' },
              { name: 'CNC milling', status: 'coming' },
              { name: 'Turning', status: 'coming' },
              { name: 'Waterjet cutting', status: 'planned' },
              { name: 'Investment casting', status: 'planned' },
              { name: 'SLA 3D printing', status: 'planned' },
            ].map(({ name, status }, i) => (
              <div key={i} className="nb-sketch" style={{ padding: 14, textAlign: 'center' }}>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 'var(--t-sm)', letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: 4, color: status === 'live' ? 'var(--nb-accent)' : 'var(--ink-4)' }}>
                  {status === 'live' ? '● live' : status === 'coming' ? 'coming online' : 'planned'}
                </div>
                <span style={{ fontFamily: 'var(--hand)', fontWeight: 700, fontSize: 'var(--t-md)', color: 'var(--ink)' }}>{name}</span>
              </div>
            ))}
          </div>

          <p className="mt-4" style={{ fontFamily: 'var(--hand)', fontSize: 'var(--t-md)', color: 'var(--ink-3)' }}>
            FDM printing is free for anyone on the platform. Other processes are part of the machining program.
          </p>
        </div>
      </section>

      {/* ══════════ HOW IT WORKS ══════════ */}
      <section className="py-12 md:py-[72px] px-5" style={{ borderTop: '1px dashed var(--ink-4)' }}>
        <div className="max-w-[1080px] mx-auto">
          <div className="max-w-[720px] mb-7">
            <div className="nb-eyebrow">process</div>
            <h2 className="mt-2" style={{ fontFamily: 'var(--hand)', fontWeight: 700, fontSize: 'var(--t-lg)', lineHeight: 1.1, color: 'var(--ink)' }}>
              How it works
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { n: '1', title: 'Design in Factory OS', desc: 'Complete a full design cycle with requirements, CAD, analysis, and DFM review.' },
              { n: '2', title: 'Submit your part', desc: 'Parts with a completed Design → Build → Prove cycle are eligible for submission.' },
              { n: '3', title: 'Review', desc: 'We evaluate documentation quality, traceability, and validation approach.' },
              { n: '4', title: 'Fabrication', desc: 'Selected parts are manufactured in our shop and shipped to your team.' },
            ].map((step) => (
              <div key={step.n} className="nb-sketch relative">
                <div
                  className="absolute -top-2.5 -left-2.5 w-7 h-7 rounded-full flex items-center justify-center"
                  style={{ border: '1.5px solid var(--ink)', background: 'var(--paper)', fontFamily: 'var(--mono)', fontWeight: 700, fontSize: 'var(--t-sm)', boxShadow: '2px 3px 0 rgba(0,0,0,0.10)' }}
                >
                  {step.n}
                </div>
                <h3 style={{ fontFamily: 'var(--hand)', fontWeight: 700, fontSize: 'var(--t-md)', color: 'var(--ink)', marginBottom: 4 }}>{step.title}</h3>
                <p style={{ fontFamily: 'var(--hand)', fontSize: 'var(--t-md)', color: 'var(--ink-2)', lineHeight: 1.4 }}>{step.desc}</p>
              </div>
            ))}
          </div>

          <p className="mt-5 text-center" style={{ fontFamily: 'var(--mono)', fontSize: 'var(--t-sm)', color: 'var(--ink-3)', letterSpacing: '0.4px' }}>
            Selection is not guaranteed. Capacity is limited.
          </p>
        </div>
      </section>

      {/* ══════════ SELECTION CRITERIA ══════════ */}
      <section className="py-12 md:py-[72px] px-5" style={{ borderTop: '1px dashed var(--ink-4)' }}>
        <div className="max-w-[720px] mx-auto">
          <div className="nb-eyebrow">evaluation</div>
          <h2 className="mt-2 mb-6" style={{ fontFamily: 'var(--hand)', fontWeight: 700, fontSize: 'var(--t-lg)', lineHeight: 1.1, color: 'var(--ink)' }}>
            Selection criteria
          </h2>

          <div className="space-y-3">
            {[
              'Quality of engineering documentation',
              'Completeness of requirement-to-test traceability',
              'Strength of validation approach',
              'Program capacity',
            ].map((item, i) => (
              <div key={i} className="nb-sketch" style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                <div
                  style={{ width: 28, height: 28, borderRadius: '50%', border: '1.5px solid var(--ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--mono)', fontWeight: 700, fontSize: 'var(--t-sm)', flexShrink: 0 }}
                >
                  {i + 1}
                </div>
                <span style={{ fontFamily: 'var(--hand)', fontSize: 'var(--t-md)', color: 'var(--ink)' }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ CLOSING ══════════ */}
      <section className="py-14 px-5 text-center" style={{ borderTop: '1px dashed var(--ink-4)' }}>
        <div className="max-w-[820px] mx-auto">
          <p style={{ fontFamily: 'var(--hand)', fontSize: 'var(--t-md)', color: 'var(--ink-3)', lineHeight: 1.2 }}>
            Most teams build parts.
          </p>
          <p style={{ fontFamily: 'var(--hand)', fontWeight: 700, fontSize: 'var(--t-md)', color: 'var(--ink)', lineHeight: 1.2, marginTop: 4 }}>
            Very few prove them.
          </p>
          <p className="mt-2 mb-8" style={{ fontFamily: 'var(--script)', fontSize: 'var(--t-md)', color: 'var(--nb-accent)', lineHeight: 1.2 }}>
            This program exists for the ones who do.
          </p>

          <div className="flex flex-wrap gap-3 justify-center">
            <a href={REQUEST_ACCESS_URL} className="nb-btn no-underline">
              Request access
            </a>
            <a href="https://demo.factory-os.com" className="nb-btn nb-btn-ghost">
              ▶ Take the 2-minute tour
            </a>
          </div>
        </div>
      </section>

      <LegalFooter />

    </div>
  );
};

export default MachiningProgram;
